import { alpha } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { checkboxClasses } from '@mui/material/Checkbox';
import { switchClasses } from '@mui/material/Switch';
import { menuItemClasses } from '@mui/material/MenuItem';
import { tableCellClasses } from '@mui/material/TableCell';
import { tableFooterClasses } from '@mui/material/TableFooter';
import { tablePaginationClasses } from '@mui/material/TablePagination';
import { tableRowClasses } from '@mui/material/TableRow';
import { toolbarClasses } from '@mui/material/Toolbar';

// Curva de salida premium (soft-skill) para feedback de botones.
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const components = {
  // Base global: superficie off-black, cifras tabulares y suavizado de fuente.
  // Un SvgIcon mide 1em × 1em y NO preserva su relación al encogerse: dentro de un flex
  // apretado (mobile a 320-375px) se deforma — es el origen de la "luna ovalada" de los días
  // de descanso. Nunca queremos que un icono se encoja, así que se fija de forma global.
  MuiSvgIcon: {
    styleOverrides: {
      root: { flexShrink: 0 }
    }
  },
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      html: {
        scrollBehavior: 'smooth',
        // Scrollbar slim y sutil (Firefox).
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.scrollbar.thumb} transparent`
      },
      body: {
        backgroundColor: theme.palette.surface.app,
        color: theme.palette.text.primary,
        fontVariantNumeric: 'tabular-nums',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        // Variables CSS mode-aware para los sx ESTÁTICOS (premiumButtonSx, etc.) que no
        // pueden leer `theme` porque se usan con object-spread. Cascadean a todos los
        // descendientes (incluidos portales de Menu/Dialog, que cuelgan de body).
        '--otx-cta-bg': theme.palette.primaryCta.bg,
        '--otx-cta-text': theme.palette.primaryCta.text,
        '--otx-surface-hover': theme.palette.surface.hover,
        // Sombras teñidas de las "premium cards" (sx estáticos por toda la app).
        '--otx-inner-highlight': theme.palette.innerHighlight,
        '--otx-card-shadow': theme.palette.shadow.card,
        '--otx-card-shadow-hover':
          theme.palette.mode === 'dark'
            ? '0 16px 38px rgba(0, 0, 0, 0.45)'
            : '0 16px 38px rgba(17, 21, 27, 0.16)',
        '--otx-btn-disabled-bg':
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.12)'
            : 'rgba(17, 21, 27, 0.08)',
        '--otx-btn-disabled-text':
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.4)'
            : 'rgba(17, 21, 27, 0.35)',
        '--otx-focus-ring':
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.7)'
            : 'rgba(17, 21, 27, 0.55)',
        // FullCalendar (styles/full-calendar.css lee estas vars; fallback oscuro allí).
        '--otx-fc-border': theme.palette.divider,
        // Línea divisora entre horas: NOTABLE y theme-aware (oscura/negra en claro, gris claro
        // resaltable en oscuro). Mejora la lectura, sobre todo en móvil.
        '--otx-fc-hour-line': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.22)' : 'rgba(17, 21, 27, 0.30)',
        // Fondo del ítem resaltado por el tour (para que "salga" sobre el overlay sin atenuarse).
        '--otx-tour-bg': theme.palette.background.paper,
        '--otx-fc-grid-bg': theme.palette.mode === 'dark' ? '#292E33' : '#FFFFFF',
        '--otx-fc-grid-bg-alt': theme.palette.mode === 'dark' ? '#212529' : '#F4F5F7',
        '--otx-fc-header-bg': theme.palette.mode === 'dark' ? '#1F2327' : '#F0F1F4',
        '--otx-fc-disabled': theme.palette.mode === 'dark' ? '#363D44' : '#E4E7EB',
        '--otx-fc-disabled-header': theme.palette.mode === 'dark' ? '#5E6A75' : '#9AA1AA',
        '--otx-fc-button-text': theme.palette.text.primary,
        // Logo de marca: invertido a blanco en oscuro, a color en claro.
        '--otx-logo-filter': theme.palette.brand.logoFilter,
        // Texto dorado legible: dorado puro en oscuro; ámbar oscuro en claro (AA sobre
        // superficies claras y sobre el tinte gold.soft).
        '--otx-gold-text': theme.palette.mode === 'dark' ? '#F9BD4E' : '#9A6B00'
      },
      // Los toasts van por ENCIMA de todo (modales 1300, drawers 1200, tooltips 1500): el
      // aviso de bienvenida tiene que seguir visible aunque la navegación monte un diálogo.
      '.notistack-SnackbarContainer': { zIndex: 1600 },
      // Scrollbar slim y sutil (WebKit/Chrome): track invisible, thumb translúcido por modo.
      '*::-webkit-scrollbar': { width: '10px', height: '10px' },
      '*::-webkit-scrollbar-track': { background: 'transparent' },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.scrollbar.thumb,
        borderRadius: '8px',
        border: '2px solid transparent',
        backgroundClip: 'content-box'
      },
      '*::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.palette.scrollbar.thumbHover
      },
      // Tablas mui-datatables en modo apilado (mobile, responsive 'vertical'): cada fila
      // es un registro completo con sus campos uno debajo de otro. Sin un separador
      // marcado se leen como un solo bloque, así que añadimos una línea blanca gruesa
      // entre filas para distinguir dónde empieza y termina cada registro. La clase
      // `responsiveStacked` solo está presente en este modo, así que desktop no se toca.
      [theme.breakpoints.down('md')]: {
        'tbody tr[class*="responsiveStacked"]': {
          borderBottom: `2px solid ${theme.palette.stackedRowBorder}`,
          paddingBottom: theme.spacing(1.25),
          marginBottom: theme.spacing(0.5)
        }
      }
    })
  },
  // Botones: el primario deja de ser azul y pasa a tinta + dorado en toda la app.
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        transition: `transform 200ms ${EASE}, background-color 200ms ${EASE}, box-shadow 200ms ${EASE}`,
        '&:active': { transform: 'scale(0.98)' },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.gold.main}`, outlineOffset: 2 }
      }),
      // CTA principal: en oscuro blanco/negro; en claro tinta navy/blanco (primaryCta).
      containedPrimary: ({ theme }) => ({
        backgroundColor: theme.palette.primaryCta.bg,
        color: theme.palette.primaryCta.text,
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: theme.palette.primaryCta.bg,
          transform: 'scale(1.02)',
          filter: 'brightness(0.94)'
        }
      }),
      outlinedPrimary: ({ theme }) => ({
        // Mode-aware: en claro el dorado profundo, nunca el brillante semitransparente.
        borderColor: alpha(theme.palette.gold.main, 0.5),
        color: theme.palette.gold.main,
        '&:hover': { borderColor: theme.palette.gold.main, backgroundColor: theme.palette.gold.soft }
      }),
      textPrimary: ({ theme }) => ({ color: theme.palette.gold.main })
    }
  },
  // Alertas informativas (los antiguos toasts azules) → monocromo, mismo lenguaje que el
  // banner de "permitir notificaciones push": superficie off-black, texto blanco, ícono
  // dorado. Las acciones de texto van en dorado; los botones contained/outlined conservan
  // su estilo de marca (CTA blanco / outline dorado).
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: '12px' },
      filledInfo: ({ theme }) => ({
        backgroundColor: theme.palette.surface.raised,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.hairline.main}`,
        boxShadow: theme.palette.shadow.alert,
        '& .MuiAlert-icon': { color: theme.palette.gold.main },
        '& .MuiAlert-action .MuiButton-text': {
          color: theme.palette.gold.main,
          fontWeight: 700
        }
      })
    }
  },
  // Inputs: el foco pasa de azul a dorado en TODA la app (outlined, filled y underline).
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        // En CLARO los inputs deben ser NOTORIOS sobre la card: relleno blanco + borde NEGRO
        // marcado + texto/placeholder oscuros (sin esto el recuadro y el texto se pierden).
        ...(theme.palette.mode === 'light' && {
          backgroundColor: '#FFFFFF',
          color: theme.palette.text.primary,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(17, 21, 27, 0.6)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(17, 21, 27, 0.82)' },
          '& input::placeholder, & textarea::placeholder': { color: 'rgba(17, 21, 27, 0.7)', opacity: 1 }
        }),
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.gold.main }
      })
    }
  },
  MuiFilledInput: {
    styleOverrides: {
      root: ({ theme }) => ({ '&:after': { borderBottomColor: theme.palette.gold.main } })
    }
  },
  MuiInput: {
    styleOverrides: {
      root: ({ theme }) => ({ '&:after': { borderBottomColor: theme.palette.gold.main } })
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      // En claro el label/placeholder flotante debe ser OSCURO (Select, DatePicker,
      // Password, etc. que no pasan por StyledTextField). El foco va en dorado.
      root: ({ theme }) => ({
        ...(theme.palette.mode === 'light' && { color: 'rgba(17, 21, 27, 0.82)' }),
        '&.Mui-focused': { color: theme.palette.gold.main }
      })
    }
  },
  MuiAutocomplete: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.surface.raised,
        // El overlay claro solo tiene sentido para levantar la superficie off-black.
        backgroundImage:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12))'
            : 'none',
        border: `1px solid ${theme.palette.hairline.main}`
      }),
      listbox: ({ theme }) => ({
        [`& .${autocompleteClasses.option}`]: {
          // Selección NEUTRA (gris sobre superficie), sin relleno dorado.
          '&[aria-selected="true"]': {
            backgroundColor: theme.palette.selected.bg,
            color: theme.palette.selected.fg,
            fontWeight: 600
          },
          [`&.${autocompleteClasses.focused}[aria-selected="true"]`]: {
            backgroundColor: theme.palette.selected.bg
          }
        }
      }),
      option: ({ theme }) => ({
        '&[aria-selected="true"]': {
          backgroundColor: theme.palette.selected.bg,
          color: theme.palette.selected.fg,
          fontWeight: 600,
          '&.Mui-focused': {
            backgroundColor: theme.palette.selected.bg
          }
        }
      })
    }
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`&.${checkboxClasses.indeterminate}, &.${checkboxClasses.checked}`]: {
          color: theme.palette.secondary.main
        }
      })
    }
  },
  // Switches en el acento dorado de marca (nunca el azul por defecto de MUI).
  MuiSwitch: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${switchClasses.switchBase}.${switchClasses.checked}`]: {
          color: theme.palette.secondary.main
        },
        [`& .${switchClasses.switchBase}.${switchClasses.checked} + .${switchClasses.track}`]: {
          backgroundColor: theme.palette.secondary.main
        }
      })
    }
  },
  MUIDataTable: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: 'initial',
        [`&.clientPayment tbody .${tableCellClasses.root}:nth-of-type(1)`]: {
          paddingLeft: 0,
          '& > div': {
            paddingLeft: theme.spacing(0.5),
            [theme.breakpoints.down('lg')]: {
              backgroundColor: theme.palette.gray0.main
            }
          }
        },
        '&.performance': {
          [`& tbody .${tableCellClasses.root}:nth-of-type(1)`]: {
            backgroundColor: theme.palette.gray0.main,
            paddingLeft: 0,
            '& > div': {
              paddingLeft: theme.spacing(0.5)
            }
          },
          [`& .${tableCellClasses.body}:nth-of-type(2)`]: {
            whiteSpace: 'nowrap'
          }
        },
        '&.user': {
          [`& .${tableCellClasses.head}:nth-of-type(2)`]: {
            paddingLeft: '32px'
          },
          [`& .${tableCellClasses.head}:nth-of-type(2), .${tableCellClasses.body}:nth-of-type(2)`]:
            {
              textAlign: 'left'
            },
          '&.user--client': {
            [`& .${tableCellClasses.body}:nth-of-type(1)`]: {
              whiteSpace: 'nowrap'
            }
          }
        },
        '&.session': {
          [`
            &:not(.session--administrator) .${tableCellClasses.body}:nth-of-type(1),
            &:not(.session--administrator) .${tableCellClasses.body}:nth-of-type(3)
          `]: {
            whiteSpace: 'nowrap'
          },
          [`.${tableCellClasses.body}:nth-of-type(1)`]: {
            backgroundColor: theme.palette.gray0.main,
            paddingLeft: 0,
            '& > div': {
              paddingLeft: theme.spacing(0.5)
            }
          },
          [`.${tableCellClasses.body}:nth-of-type(3) .MuiBox-root`]: {
            justifyContent: 'flex-start'
          },
          [`&.session--administrator .${tableCellClasses.body}:nth-of-type(2)`]: {
            whiteSpace: 'nowrap'
          },
          [`&.session--administrator .${tableCellClasses.body}:nth-of-type(4) .MuiBox-root`]: {
            justifyContent: 'flex-start'
          },
          [`&.session--administrator .${tableCellClasses.body}:nth-of-type(5) .MuiBox-root`]: {
            justifyContent: 'flex-start'
          }
        },
        [`&.latestPerformanceClient .${tableRowClasses.root}.${tableRowClasses.hover}`]: {
          backgroundColor: 'transparent'
        },
        [`& .${tableFooterClasses.root}`]: {
          [`& .${tableCellClasses.root}`]: {
            [theme.breakpoints.down('lg')]: {
              padding: 0
            }
          },
          [`& .${tablePaginationClasses.spacer}`]: {
            [theme.breakpoints.down('sm')]: {
              display: 'none'
            }
          },
          [`& .${toolbarClasses.root}`]: {
            [theme.breakpoints.down('sm')]: {
              justifyContent: 'center',
              padding: 0
            }
          }
        }
      })
    }
  },
  MUIDataTableToolbarSelect: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: theme.palette.gray2.main,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        boxShadow: 'none',
        '& + *': {
          marginTop: '-9px'
        }
      }),
      title: ({ theme }) => ({
        fontSize: theme.typography.pxToRem(14),
        paddingLeft: '16px',
        [theme.breakpoints.up('lg')]: {
          fontSize: theme.typography.pxToRem(16)
        }
      })
    }
  },
  MUIDataTableSelectCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Hacer sticky en mobile para todas las tablas
        [theme.breakpoints.down('lg')]: {
          position: 'sticky',
          left: 0,
          zIndex: 100,
          backgroundColor: theme.palette.gray0.main,
          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.15)'
        }
      }),
      headerCell: ({ theme }) => ({
        // Mismo negro que el resto de la cabecera (sin el "pedazo blanco" del checkbox).
        backgroundColor: theme.palette.ink.main,
        // Hacer sticky en mobile
        [theme.breakpoints.down('lg')]: {
          position: 'sticky',
          left: 0,
          zIndex: 101,
          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.2)'
        }
      })
    }
  },
  MUIDataTableHeadCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Cabecera de columnas: fondo NEGRO + texto BLANCO en ambos modos (alto contraste,
        // sobre todo en claro donde el gris se perdía).
        backgroundColor: theme.palette.ink.main,
        color: '#FFFFFF',
        fontSize: theme.typography.pxToRem(12),
        padding: '6px',
        textAlign: 'center',
        textTransform: 'uppercase',
        [theme.breakpoints.up('lg')]: {
          fontSize: theme.typography.pxToRem(14),
          padding: theme.spacing(2)
        },
        '& .multiline-header-label': {
          lineHeight: '18px',
          '&--last': {
            fontSize: '12px',
            lineHeight: '24px',
            textTransform: 'lowercase'
          }
        }
      })
    }
  },
  MUIDataTableBodyCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.pxToRem(12),
        padding: theme.spacing(0.5),
        textAlign: 'center',
        [theme.breakpoints.up('lg')]: {
          fontSize: theme.typography.pxToRem(14),
          padding: theme.spacing(2)
        }
      })
    }
  },
  MUIDataTableFooter: {
    styleOverrides: {
      root: () => ({
        [`& .${tableCellClasses.root}`]: {
          padding: 0
        }
      })
    }
  },
  MUIDataTablePagination: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          width: 'auto'
        },
        [`.${tablePaginationClasses.actions}`]: {
          [theme.breakpoints.down('sm')]: {
            columnGap: theme.spacing(3),
            display: 'flex'
          },
          button: {
            [theme.breakpoints.down('sm')]: {
              padding: 0
            }
          }
        }
      }),
      navContainer: ({ theme }) => ({
        columnGap: theme.spacing(2),
        justifyContent: 'center',
        [theme.breakpoints.up('sm')]: {
          columnGap: theme.spacing(0),
          justifyContent: 'flex-end'
        }
      })
    }
  },
  // Popover de TODO dropdown (Select / TextField select / Menu): superficie off-black,
  // hairline, radio y sombra teñida. Así cada dropdown hereda el estilo prefijado sin
  // tener que repetir MenuProps en cada componente.
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.surface.raised,
        backgroundImage: 'none',
        border: `1px solid ${theme.palette.hairline.main}`,
        borderRadius: 12,
        boxShadow: theme.palette.shadow.menu
      }),
      list: { paddingTop: 4, paddingBottom: 4 }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Hover y selección NEUTROS (una sola familia de grises); el dorado ya no
        // rellena opciones de menú — queda para microacentos.
        '&:hover': { backgroundColor: theme.palette.surface.overlay },
        [`&.${menuItemClasses.selected}`]: {
          backgroundColor: theme.palette.selected.bg,
          color: theme.palette.selected.fg,
          fontWeight: 600,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.selected.bg,
            color: theme.palette.selected.fg,
            fontWeight: 600
          }
        }
      })
    }
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottomColor: theme.palette.divider,
        [`&.${tableCellClasses.head}`]: {
          '&:first-of-type': {
            borderTopLeftRadius: '10px'
          },
          '&:last-child': {
            borderTopRightRadius: '10px'
          }
        },
        [`&.${tableCellClasses.head}, &.${tableCellClasses.footer}`]: {
          borderBottom: 0
        }
      })
    }
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:last-child': {
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none'
          }
        },
        [`&.${tableRowClasses.hover}:hover`]: {
          backgroundColor: 'transparent',
          [theme.breakpoints.up('lg')]: {
            // Hover neutro mode-aware (el tinte dorado era invisible en claro).
            backgroundColor: theme.palette.surface.overlay
          }
        },
        [`&.${tableRowClasses.selected}`]: {
          [`& .${tableCellClasses.root}:first-of-type::before`]: {
            right: '-3px',
            width: 'calc(100% - 3px)'
          },
          [`& .${tableCellClasses.root}:last-child::before`]: {
            left: '-3px',
            width: 'calc(100% - 3px)'
          }
        }
      })
    }
  }
};

export default components;
